import DocumentDownloadIcon from '../../../assets/svg/download.svg';
import {updateAssetStatus} from '../../../redux/actions/Actions';
import {appStore} from '../../../redux/createStore';
import {postAPICall} from '../../../services/apiCallsHandler';
import {
  SPAIN_COUNTRY_CODE,
  SPAIN_WARRANTY_ASSET,
} from '../../../utils/constant';
import Smartlook from '../../../utils/smartlook';
import {openDocumentFromBase64, sendScreenViewNR} from '../../../utils/util';

import {
  Button,
  Card,
  CardContent,
  ListItemButton,
  ListItemIcon,
  Typography,
  useToastProvider,
} from '@deere/fuel-react';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import './lienRequestassetDetails.css';

const LienRequestassetDetails = (props) => {
  const [assetsDetails, setAssetsDetails] = React.useState([]);
  const [isMobile, setIsMobile] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [loadingAssets, setLoadingAssets] = React.useState(new Set()); // Track loading per asset

  const {enqueueToast} = useToastProvider();

  const {t} = useTranslation();

  const checkIsMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  React.useEffect(() => {
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  React.useEffect(() => {
    const updatedSelectedItems = [];

    for (const asset of props.assets) {
      if (!asset.oid && selectedItems.includes(asset.serialNumber)) {
        updatedSelectedItems.push(asset.serialNumber);
      }
    }
    props.setSelectedAssetDetails(updatedSelectedItems);
  }, [selectedItems]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      const allSerialNumbers = assetsDetails.map(
        (asset) => asset.SERIAL_NUMBER.value
      );
      setSelectedItems(allSerialNumbers);
    } else {
      setSelectedItems([]);
    }
  };

  const handleItemSelect = (serialNumber) => {
    let updatedSelectedItems = [...selectedItems];
    if (updatedSelectedItems.includes(serialNumber)) {
      updatedSelectedItems = updatedSelectedItems.filter(
        (item) => item !== serialNumber
      );
    } else {
      updatedSelectedItems.push(serialNumber);
    }
    setSelectedItems(updatedSelectedItems);

    setSelectAll(updatedSelectedItems.length === assetsDetails.length);
  };

  const handleLienDownload = (serialNumber, checkUpdateDbStatus) => {
    setLoadingAssets((prev) => new Set(prev).add(serialNumber));

    let asset = props.assets?.find(
      (asset) => asset.serialNumber === serialNumber
    );
    postAPICall(
      'liendownload',
      {
        oid: asset?.oid,
        contractCode: props.selectedContractNumber,
        checkAndUpdate: checkUpdateDbStatus,
        ...(checkUpdateDbStatus && {serialNumber}),
      },
      {
        errorTag: 'GET_LIEN_DOCUMENT_DOWNLOAD',
      }
    )
      .then((response) => {
        if (response.status === 200) {
          switch (response?.body.statusCode) {
            case 200:
              if (response?.body?.data) {
                const base64String = response.body.data.document;
                openDocumentFromBase64(
                  base64String,
                  'application/pdf',
                  'Lien_Deregistration_Letter.pdf'
                );
              }
              if (checkUpdateDbStatus) {
                appStore.dispatch(
                  updateAssetStatus({
                    contractNumber: props.selectedContractNumber,
                    serialNumber: serialNumber,
                    updatedFields: {lienDocumentStatus: 'Available'},
                  })
                );
              }
              break;
            case 422:
              if (
                response.body.data.resultCode ===
                'Cancellation.SignedCertificateNotFound'
              ) {
                enqueueToast({
                  variant: 'success',
                  title: t('DOCUMENT_BEING_PREPARED'),
                  isCloseable: true,
                });
              }
              break;
            default:
              enqueueToast({
                variant: 'error',
                title: t('LIEN_DE_REGISTRATION_LETTER_DOWNLOAD_FAILED'),
                isCloseable: true,
              });
              break;
          }
        }
      })
      .finally(() => {
        setLoadingAssets((prev) => {
          const newSet = new Set(prev);
          newSet.delete(serialNumber);
          return newSet;
        });
      });
  };

  const handleDMDdownload = async (asset, checkAndUpdate) => {
    setLoadingAssets((prev) => new Set(prev).add(asset.value));

    await postAPICall(
      'downloadUrl',
      [
        {
          guid: asset.guid,
          serialNumber: asset.value,
          requestId: asset.requestNumber,
          requestStatus: asset.status,
          requestType: 'Download document',
          name: 'LIEN_DE_REGISTRATION_LETTERS',
        },
      ],
      {
        error_tag: 'GET_DMD_LIEN_DOCUMENT',
        redirectToOops: false,
      }
    )
      .then((res) => {
        if (res.body?.length) {
          if (checkAndUpdate) {
            appStore.dispatch(
              updateAssetStatus({
                contractNumber: props.selectedContractNumber,
                serialNumber: asset.value,
                updatedFields: {lienDocumentStatus: 'Available'},
              })
            );
          }
          if (globalThis.ReactNativeWebView) {
            globalThis.ReactNativeWebView.postMessage(
              JSON.stringify({
                docUrl: res.body[0],
                docName: `JOHNDEERE_DOCUMENT_${Date.now()}.pdf`,
              })
            );
          } else {
            globalThis.open(res.body[0], '_blank');
          }
        } else if (checkAndUpdate) {
          enqueueToast({
            variant: 'success',
            title: t('DOCUMENT_BEING_PREPARED'),
            isCloseable: true,
          });
        }
      })
      .finally(() => {
        setLoadingAssets((prev) => {
          const newSet = new Set(prev);
          newSet.delete(asset.value);
          return newSet;
        });
      });
  };

  const renderActionButton = (asset) => {
    const isLoading = loadingAssets.has(asset.value);
    let content;
    if (asset.status === 'Available') {
      content = (
        <div id="documentDownloadButtonWrapper">
          <ListItemButton
            onClick={() => {
              if (asset.guid) {
                handleDMDdownload(asset, false);
              } else {
                handleLienDownload(asset.value, false);
              }
            }}
            className="actionItemButton"
            disabled={isLoading}
          >
            <ListItemIcon>
              <img src={DocumentDownloadIcon} alt="Download Icon" />
            </ListItemIcon>
            <span>{isLoading ? t('DOWNLOADING') : t('DOWNLOAD')}</span>
          </ListItemButton>
        </div>
      );
    } else {
      content = (
        <div id="documentStatusButtonWrapper">
          <Button
            id="documentStatusButton"
            onClick={() => {
              if (asset.guid) {
                handleDMDdownload(asset, true);
              } else {
                handleLienDownload(asset.value, true);
              }
            }}
            variant="secondary"
            disabled={isLoading}
          >
            {isLoading ? t('CHECKING') : t('CHECK_DOCUMENT_STATUS')}
          </Button>
        </div>
      );
    }
    return content;
  };
  const renderMobileView = () => {
    return assetsDetails?.map((asset, index) => (
      <div
        className="DownloadItemWrapper assetDetailsMobileContainer assetDetailsCard"
        key={
          asset?.SERIAL_NUMBER?.value ||
          `key-${asset?.REGISTRATION_NUMBER?.value}`
        }
      >
        <div id="mobileAssetDetailsCardRow1">
          <div className="checkBoxWrapper">
            <input
              className="downloadItemCheckBox"
              type="checkbox"
              id={`checkbox-${index}`}
              data-testid={`Checkbox-${index}`}
              name={`Checkbox-${index}`}
              checked={
                asset.ACTION.value
                  ? false
                  : selectedItems.includes(asset.SERIAL_NUMBER.value)
              }
              disabled={asset.ACTION.value}
              onChange={() => handleItemSelect(asset?.SERIAL_NUMBER?.value)}
            />
          </div>
          <div>
            <div className="DownloadItemContract">{t('SERIAL_NUMBER')}</div>
            <div className="DownloadItemTitle">{asset.SERIAL_NUMBER.value}</div>
          </div>
        </div>
        <div className="mobile-divider-line"></div>

        <div id="mobileAssetDetailsCardRow2">
          <div>
            <div className="DownloadItemContract">
              {t('REGISTRATION_NUMBER')}
            </div>
            <div className="DownloadItemTitle">
              {asset.REGISTRATION_NUMBER.value}
            </div>
          </div>
          <div>
            <div className="DownloadItemContract">{t('STATUS')}</div>
            <div className="DownloadItemTitle" id="statusText">
              {asset.STATUS.value}
            </div>
          </div>
        </div>
        {asset.ACTION.value && (
          <div id="mobileAssetDetailsCardRow3">
            {renderActionButton(asset.ACTION)}
          </div>
        )}
      </div>
    ));
  };

  const renderWebView = () => {
    return (
      <Card>
        <CardContent>
          <div className="lienAssetContainer lienAssetDetailsContainer">
            <div className="lienAssetHeader">
              <input
                type="checkbox"
                id="selectAllWeb"
                data-testid="SelectAllWebCheckbox"
                checked={
                  !assetsDetails.every((asset) => asset.ACTION.value) &&
                  selectAll
                }
                onChange={handleSelectAll}
                disabled={assetsDetails.every((asset) => asset.ACTION.value)}
              />
              {assetsDetails?.length > 0 &&
                Object.keys(assetsDetails[0]).map((key) => (
                  <Typography variant="label" key={key}>
                    {t(key)}
                  </Typography>
                ))}
            </div>
            <div className="lienAssetValues">
              {assetsDetails?.map((asset, index) => (
                <div
                  className="lienAssetBody"
                  key={
                    asset.SERIAL_NUMBER.value || asset.REGISTRATION_NUMBER.value
                  }
                >
                  <input
                    type="checkbox"
                    id={`checkbox-${index}`}
                    data-testid={`Checkbox-${index}`}
                    checked={
                      asset.ACTION.value
                        ? false
                        : selectedItems.includes(asset.SERIAL_NUMBER.value)
                    }
                    onChange={() => handleItemSelect(asset.SERIAL_NUMBER.value)}
                    disabled={asset.ACTION.value}
                  />
                  {Object.entries(asset).map(([key, field]) => {
                    let content;
                    if (key === 'ACTION' && field.value) {
                      content = renderActionButton(field);
                    } else if (field?.isTextField) {
                      content = (
                        <Typography key={key}>{field?.value}</Typography>
                      );
                    } else {
                      content = field?.value;
                    }
                    return content;
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  React.useEffect(() => {
    if (props.selectedContractNumber && props.lienDeregistrationData) {
      const assetsArray =
        props.lienDeregistrationData[props.selectedContractNumber]?.assets;

      const transformedAssets =
        props.countryCode === SPAIN_COUNTRY_CODE
          ? assetsArray
              ?.filter(
                (asset) =>
                  asset?.model?.modelName.toLowerCase() !== SPAIN_WARRANTY_ASSET
              )
              ?.map((asset) => ({
                SERIAL_NUMBER: {
                  label: t('SERIAL_NUMBER'),
                  value: asset.serialNumber ?? 'NA',
                  isTextField: true,
                },
                REGISTRATION_NUMBER: {
                  label: t('REGISTRATION_NUMBER'),
                  value: asset.registrationNumber ?? 'NA',
                  isTextField: true,
                },
                STATUS: {
                  label: t('STATUS'),
                  value: t(
                    asset.lienDocumentStatus
                      ?.toUpperCase()
                      .replaceAll(/\s+/g, '_')
                  ),
                  isTextField: true,
                },
                ACTION:
                  asset.oid || asset.guid
                    ? {
                        label: t('ACTION'),
                        value: asset.serialNumber,
                        status: asset.lienDocumentStatus,
                        requestNumber: asset.requestNumber,
                        guid: asset.guid,
                        isTextField: false,
                      }
                    : {label: t('ACTION'), value: null},
              }))
          : null;

      setAssetsDetails(transformedAssets);

      const allSerialNumbers = transformedAssets?.map(
        (asset) => asset.SERIAL_NUMBER.value
      );
      setSelectedItems(allSerialNumbers);
      setSelectAll(true);
    }
  }, [
    props.selectedContractNumber,
    props.lienDeregistrationData,
    loadingAssets,
  ]);

  React.useEffect(() => {
    sendScreenViewNR('lienDeregistrationRequest');
    Smartlook.navigation('lienDeregistrationRequest');
  }, []);

  return (
    <div className="scheduleInstallmentsWrapper lienRequestassetDetailsWrapper">
      <div>
        <div className="lienRequestHeader">
          <div className="title">{t('LIEN_DE_REGISTRATION_LETTERS')}</div>
          {isMobile && (
            <Button
              variant="text"
              className="downloadItemCheckBox"
              type="checkbox"
              id="DownloadAllCheckbox"
              data-testid="DownloadAllCheckbox"
              name="DownloadAllCheckbox"
              onClick={handleSelectAll}
            >
              {t('SELECT_ALL')}
            </Button>
          )}
        </div>
        <div className="raiseDownloadRequestSubTitle">
          {t('LIEN_DE_REGISTRATION_LETTERS_SUBTITLE')}
        </div>
      </div>

      {isMobile ? renderMobileView() : renderWebView()}
    </div>
  );
};

export const mapStateToProps = (state) => ({
  lienDeregistrationData: state.appStates.lienDeregistrationData,
  countryCode: state.appStates.allContracts?.countryCode,
});

LienRequestassetDetails.propTypes = {
  assets: PropTypes.array,
  setSelectedAssetDetails: PropTypes.func,
  selectedContractNumber: PropTypes.string,
  lienDeregistrationData: PropTypes.object,
  countryCode: PropTypes.string,
};

export default connect(mapStateToProps)(LienRequestassetDetails);
